import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Music, Disc3, Headphones, Heart, Clock, Users,
  Loader2, AlertCircle, Unplug, Star, BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";

function ArtistCard({ artist, rank }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
      <span className="text-xs text-slate-600 w-5 font-bold">{rank}</span>
      {artist.image ? (
        <img src={artist.image} alt={artist.name} className="w-10 h-10 rounded-full object-cover" />
      ) : (
        <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
          <Users className="w-4 h-4 text-slate-600" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-white truncate">{artist.name}</p>
        <p className="text-xs text-slate-500 truncate">{(artist.genres || []).join(", ") || "—"}</p>
      </div>
    </div>
  );
}

function TrackCard({ track, rank }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
      <span className="text-xs text-slate-600 w-5 font-bold">{rank}</span>
      {track.album_art ? (
        <img src={track.album_art} alt={track.name} className="w-10 h-10 rounded-lg object-cover" />
      ) : (
        <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
          <Disc3 className="w-4 h-4 text-slate-600" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-white truncate">{track.name}</p>
        <p className="text-xs text-slate-500 truncate">{track.artist}</p>
      </div>
    </div>
  );
}

export default function SpotifyPage() {
  const [searchParams] = useSearchParams();
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [profile, setProfile] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [activeTimeRange, setActiveTimeRange] = useState("medium_term");

  useEffect(() => {
    checkStatus();
    const code = searchParams.get("code");
    if (code) exchangeToken(code);
  }, []);

  const checkStatus = async () => {
    try {
      const res = await api.get("/spotify/status");
      setConnected(res.data.connected);
      setConfigured(res.data.configured);
      setProfile(res.data.connection);
      if (res.data.connected) loadInsights();
    } catch { /* ignore */ }
  };

  const exchangeToken = async (code) => {
    setConnecting(true);
    try {
      const redirectUri = window.location.origin + "/connections/spotify/callback";
      const res = await api.post("/spotify/exchange-token", { code, redirect_uri: redirectUri });
      toast.success("Spotify connected!");
      setConnected(true);
      setProfile(res.data.connection);
      window.history.replaceState({}, "", "/connections/spotify");
      loadInsights();
    } catch {
      toast.error("Failed to connect Spotify");
    } finally {
      setConnecting(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await api.get("/spotify/auth-url");
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      } else {
        toast.error("Spotify credentials not configured");
        setConnecting(false);
      }
    } catch {
      toast.error("Failed to get auth URL");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.post("/spotify/disconnect");
      setConnected(false);
      setProfile(null);
      setInsights(null);
      toast.success("Spotify disconnected");
    } catch {
      toast.error("Failed to disconnect");
    }
  };

  const loadInsights = async () => {
    setLoadingInsights(true);
    try {
      const res = await api.get("/spotify/insights");
      setInsights(res.data);
    } catch (err) {
      toast.error("Failed to load music insights");
    } finally {
      setLoadingInsights(false);
    }
  };

  const timeRanges = [
    { key: "short_term", label: "Last 4 Weeks" },
    { key: "medium_term", label: "Last 6 Months" },
    { key: "long_term", label: "All Time" },
  ];

  const topArtists = insights?.[`top_artists_${activeTimeRange}`] || [];
  const topTracks = insights?.[`top_tracks_${activeTimeRange}`] || [];
  const topGenres = insights?.top_genres || [];
  const recentlyPlayed = insights?.recently_played || [];
  const savedAlbums = insights?.saved_albums || [];

  return (
    <div className="p-6 md:p-12 max-w-6xl mx-auto" data-testid="spotify-page">
      <div className="mb-8">
        <p className="text-xs tracking-[0.3em] uppercase text-[#1DB954] mb-2 font-medium">Music</p>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Spotify
        </h1>
        <p className="text-sm text-slate-400 mt-1">Your music personality and listening insights</p>
      </div>

      {!configured && !connected && (
        <div className="bg-white/[0.03] backdrop-blur-xl border border-amber-500/20 rounded-2xl p-5 mb-8 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-amber-200 font-medium">Spotify App Not Configured</p>
            <p className="text-xs text-slate-400 mt-0.5">
              Add your <strong className="text-white">SPOTIFY_CLIENT_ID</strong> and{" "}
              <strong className="text-white">SPOTIFY_CLIENT_SECRET</strong> to the backend environment.
              Get them from the <a href="https://developer.spotify.com/dashboard" target="_blank" rel="noopener noreferrer" className="text-[#1DB954] underline">Spotify Developer Dashboard</a>.
            </p>
          </div>
        </div>
      )}

      {connecting && (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 text-[#1DB954] animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-400">Connecting to Spotify...</p>
        </div>
      )}

      {!connected && !connecting ? (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-10 text-center max-w-md mx-auto"
        >
          <div className="w-16 h-16 rounded-2xl bg-[#1DB954]/10 flex items-center justify-center mx-auto mb-6">
            <Music className="w-8 h-8 text-[#1DB954]" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
            Connect Spotify
          </h2>
          <p className="text-sm text-slate-400 mb-8 leading-relaxed">
            Discover your music personality — top artists, favorite genres, listening habits, and more.
          </p>
          <Button
            onClick={handleConnect}
            disabled={connecting || !configured}
            data-testid="spotify-connect-btn"
            className="bg-[#1DB954] text-black hover:bg-[#1ed760] rounded-full px-8 py-6 font-bold"
          >
            <Music className="w-4 h-4 mr-2" /> Connect with Spotify
          </Button>
        </motion.div>
      ) : connected && !connecting ? (
        <div>
          {/* Profile Header */}
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <div className="flex items-center gap-4">
              {profile?.profile_image ? (
                <img src={profile.profile_image} alt="" className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#1DB954]/10 flex items-center justify-center">
                  <Music className="w-6 h-6 text-[#1DB954]" />
                </div>
              )}
              <div>
                <p className="text-lg font-semibold text-white" style={{ fontFamily: 'Manrope' }}>
                  {profile?.display_name || "Spotify User"}
                </p>
                <p className="text-xs text-slate-500 capitalize">{profile?.product || "free"} account</p>
              </div>
            </div>
            <Button
              onClick={handleDisconnect}
              variant="outline"
              data-testid="spotify-disconnect-btn"
              className="border-white/10 text-slate-400 hover:text-red-400 hover:border-red-400/30 rounded-full"
            >
              <Unplug className="w-4 h-4 mr-2" /> Disconnect
            </Button>
          </div>

          {loadingInsights ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 text-[#1DB954] animate-spin mx-auto mb-3" />
              <p className="text-sm text-slate-400">Analyzing your music taste...</p>
            </div>
          ) : insights ? (
            <div className="space-y-8">
              {/* Time Range Selector */}
              <div className="flex gap-1.5 bg-white/[0.02] border border-white/[0.06] rounded-xl p-1.5 w-fit">
                {timeRanges.map((tr) => (
                  <button
                    key={tr.key}
                    onClick={() => setActiveTimeRange(tr.key)}
                    data-testid={`time-range-${tr.key}`}
                    className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                      activeTimeRange === tr.key
                        ? "bg-[#1DB954]/10 text-[#1DB954] border border-[#1DB954]/20"
                        : "text-slate-500 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    {tr.label}
                  </button>
                ))}
              </div>

              {/* Top Genres */}
              {topGenres.length > 0 && (
                <div>
                  <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <BarChart3 className="w-4 h-4 text-[#1DB954]" /> Your Top Genres
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {topGenres.map(([genre, count], i) => (
                      <Badge
                        key={genre}
                        variant="outline"
                        className="text-xs px-3 py-1.5 rounded-full capitalize"
                        style={{
                          borderColor: `rgba(29, 185, 84, ${0.6 - i * 0.05})`,
                          color: `rgba(29, 185, 84, ${1 - i * 0.06})`,
                          background: `rgba(29, 185, 84, ${0.08 - i * 0.005})`,
                        }}
                      >
                        {genre}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Artists & Tracks Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Artists */}
                <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
                  <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Star className="w-4 h-4 text-[#1DB954]" /> Top Artists
                  </h2>
                  <div className="space-y-1">
                    {topArtists.map((artist, i) => (
                      <ArtistCard key={artist.id || i} artist={artist} rank={i + 1} />
                    ))}
                    {topArtists.length === 0 && (
                      <p className="text-sm text-slate-500 py-6 text-center">No data for this period</p>
                    )}
                  </div>
                </div>

                {/* Top Tracks */}
                <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
                  <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Headphones className="w-4 h-4 text-[#1DB954]" /> Top Tracks
                  </h2>
                  <div className="space-y-1">
                    {topTracks.map((track, i) => (
                      <TrackCard key={track.id || i} track={track} rank={i + 1} />
                    ))}
                    {topTracks.length === 0 && (
                      <p className="text-sm text-slate-500 py-6 text-center">No data for this period</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Recently Played */}
              {recentlyPlayed.length > 0 && (
                <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
                  <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Clock className="w-4 h-4 text-[#1DB954]" /> Recently Played
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {recentlyPlayed.slice(0, 8).map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02]">
                        {item.album_art ? (
                          <img src={item.album_art} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-white/5 shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-white truncate">{item.name}</p>
                          <p className="text-[10px] text-slate-500 truncate">{item.artist}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Saved Albums */}
              {savedAlbums.length > 0 && (
                <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
                  <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Heart className="w-4 h-4 text-[#1DB954]" /> Saved Albums
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                    {savedAlbums.slice(0, 10).map((album, i) => (
                      <div key={i} className="group">
                        {album.image ? (
                          <img src={album.image} alt={album.name} className="w-full aspect-square rounded-xl object-cover mb-2" />
                        ) : (
                          <div className="w-full aspect-square rounded-xl bg-white/5 mb-2 flex items-center justify-center">
                            <Disc3 className="w-8 h-8 text-slate-700" />
                          </div>
                        )}
                        <p className="text-xs font-medium text-white truncate">{album.name}</p>
                        <p className="text-[10px] text-slate-500 truncate">{album.artist}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
